import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Chip,
  Grid,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { yellow } from "@mui/material/colors";
import BasicMap from "components/BasicMap";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { compact } from "lodash-es";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";

import { useWasteFacility } from "./useWasteFacility";

export const FacilityDetailsTile = () => {
  return (
    <DashboardTile size={{ sm: 12, md: 7 }}>
      <FacilityDetailsTileContent />
    </DashboardTile>
  );
};

const FacilityDetailsTileContent = () => {
  const theme = useTheme();
  const { facilityId } = useParams<{ facilityId: string }>();
  const { tenantId } = useTenant();
  const location = useLocation();

  const { data } = useWasteFacility(facilityId);
  const { latitude, longitude, name, streetAddress1, streetAddress2, city, state, zip, county } =
    data.facility || {};

  // Get EPA ID statuses which includes both database EPA IDs
  // and mapped EPA IDs from epaIdGroupMappings
  const epaIdStatuses = data.facility?.epaIdStatuses ?? [];

  const hasLatAndLong = typeof latitude === "number" && typeof longitude === "number";

  useBreadcrumb({ label: name ?? "" });

  return (
    <>
      <DashboardTile.Header
        title="Facility Details"
        infoTooltip="See basic information about your facility. If any of this information is incorrect or needs to be updated, please reach out to Encamp."
      />
      <DashboardTile.ContentArea>
        <Grid container spacing={5}>
          {hasLatAndLong && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack spacing={2}>
                <Box height={"262px"}>
                  <BasicMap latitude={latitude} longitude={longitude} />
                </Box>
                <Typography variant="caption">{[latitude, longitude].join(", ")}</Typography>
              </Stack>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: hasLatAndLong ? 6 : 12 }}>
            <Stack spacing={2}>
              {epaIdStatuses.length > 0 && (
                <Stack alignItems="flex-start" spacing={1} gap={1}>
                  {epaIdStatuses.length > 1 && (
                    <Alert severity="warning" icon={<WarningAmber />}>
                      There are multiple EPA IDs associated with this facility
                    </Alert>
                  )}
                  <Stack spacing={1}>
                    {epaIdStatuses.map((epaIdStatus) => (
                      <Stack
                        spacing={2}
                        key={epaIdStatus.epaId}
                        alignItems="center"
                        flexWrap="wrap"
                        direction="row"
                      >
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          component="a"
                          href={`https://rcrapublic.epa.gov/rcra-hwip/search/results/site/${epaIdStatus.epaId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {epaIdStatus.epaId}
                          <Tooltip title="View RCRA Hazardous Waste Information Portal in new tab">
                            <IconButton>
                              <OpenInNewIcon />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                        {epaIdStatus.isInactive && (
                          <Chip
                            label="EPA ID is inactive"
                            sx={{
                              backgroundColor: yellow[400],
                              color: theme.palette.text.primary,
                            }}
                          />
                        )}
                        {!epaIdStatus.isPrimaryForFacility && epaIdStatus.primaryFacility && (
                          <Typography variant="body2" color="textSecondary">
                            (Waste reported through{" "}
                            <Link
                              component={RouterLink}
                              to={`/o/${tenantId}/waste/facilities/${epaIdStatus.primaryFacility.id}${location.search}`}
                            >
                              {epaIdStatus.primaryFacility.name}
                            </Link>
                            )
                          </Typography>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              )}

              <Typography variant="h4">{name}</Typography>
              <Typography variant="body1">
                {compact([
                  compact([streetAddress1, streetAddress2, city]).join(" "),
                  compact([state, zip]).join(" "),
                ]).join(", ")}
              </Typography>
              {county && (
                <Typography variant="body1">
                  {county.endsWith(" County") ? county : `${county} County`}
                </Typography>
              )}
            </Stack>
          </Grid>
        </Grid>
      </DashboardTile.ContentArea>
    </>
  );
};
